import { Component, OnInit, OnDestroy, ViewEncapsulation, ViewChild, Inject, HostListener } from '@angular/core';
import { DocumentMediaModel } from '@phdsoft/common';
import { NgxExtendedPdfViewerComponent } from 'ngx-extended-pdf-viewer';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AdminDocumentsService } from '@app/shared/services/admin-documents.service';
import { Subscription } from 'rxjs';


@Component({
  selector: 'phdsoft-pdf-viewer', // this is the software manual pdf viewer, not a generic pdf viewer
  templateUrl: './pdf-viewer.component.html',
  styleUrls: ['./pdf-viewer.component.scss'],
})
export class PdfViewerComponent implements OnInit, OnDestroy {
  @ViewChild(NgxExtendedPdfViewerComponent) pdfViewer: NgxExtendedPdfViewerComponent;
  
  public pdfSrc: string | Uint8Array;
  public loading = true;
  public error = false;
  public title: string = 'PDF Viewer';
  public visible: boolean = true;

  private manualSub?: Subscription;

  constructor(
    private dialogRef: MatDialogRef<PdfViewerComponent>,
    private adminDocumentsService: AdminDocumentsService,
    @Inject(MAT_DIALOG_DATA) public data: {
      pdfUrl?: string;
      title?: string;
    }
  ) {
    if (this.data?.title) {
      this.title = this.data.title;
    }
  }

  onLanguageChanged(newLanguage: string): void {
    this.loading = true;
    this.error = false;
    this.pdfSrc = null;

    // Load from in-memory cache (or fetch and then cache) as Uint8Array for instant reopen
    this.manualSub?.unsubscribe();
    this.manualSub = this.adminDocumentsService.getSoftwareManualCached(newLanguage).subscribe({
      next: (bytes) => {
        this.error = false;
        // Clone again to ensure a fresh buffer instance for the viewer
        this.pdfSrc = bytes.slice();
      },
      error: (err) => {
        console.error('Error loading manual PDF:', err);
        this.error = true;
        this.loading = false;
      }
    });
  }

  ngOnInit(): void {
    this.pdfSrc = null;
    this.loading = true;
    this.error = false;

    this.loadPdf();
  }

  ngOnDestroy(): void {
    if (this.pdfViewer) {
      try {
        this.pdfViewer.page = 1;
        this.pdfViewer.ngOnDestroy();
      } catch (error) {
        console.warn('Error during PDF viewer cleanup:', error);
      }
    }

    this.manualSub?.unsubscribe();

    this.pdfSrc = null;
  }

  private loadPdf(): void {
    try {
      let url = this.data?.pdfUrl;
      
      if (url) {
        this.error = false;
        this.loading = true;
        
        console.log('Loading PDF from:', url);
        // If this is the software manual endpoint, use cached bytes for instant reopen
        if (this.isSoftwareManualUrl(url)) {
          const language = this.getLanguageFromUrl(url);
          this.manualSub?.unsubscribe();
          this.manualSub = this.adminDocumentsService.getSoftwareManualCached(language).subscribe({
            next: (bytes) => {
              this.error = false;
              // Clone again to ensure a fresh buffer instance for the viewer
              this.pdfSrc = bytes.slice();
            },
            error: (err) => {
              console.error('Error loading manual PDF:', err);
              this.error = true;
              this.loading = false;
            }
          });
        } else {
          this.pdfSrc = url;
        }
      } else {
        console.error('No valid PDF URL provided');
        this.error = true;
        this.loading = false;
      }
    } catch (err) {
      console.error('Error loading PDF:', err);
      this.error = true;
      this.loading = false;
    }
  }

  onPdfLoaded(pdf: any): void {
    this.loading = false;
    console.log('PDF loaded with', pdf.numPages, 'pages');

    setTimeout(() => {
      this.resetToFirstPage();
    }, 500);
  }

  private isSoftwareManualUrl(url: string): boolean {
    return url.includes('/Documents/software-manual');
  }

  private getLanguageFromUrl(url: string): string | undefined {
    try {
      const parsed = new URL(url);
      const lang = parsed.searchParams.get('language');
      return lang || undefined;
    } catch {
      // Fallback: naive parse
      const match = /[?&]language=([^&#]+)/.exec(url);
      return match ? decodeURIComponent(match[1]) : undefined;
    }
  }

  onPdfError(error: any): void {
    console.error('PDF Error:', error);
    this.error = true;
    this.loading = false;
    
    if (error && error.message) {
      if (error.message.includes('404')) {
        console.error('PDF file not found. Please check the URL or contact administrator.');
      } else if (error.message.includes('401') || error.message.includes('403')) {
        console.error('Access denied. Please check your permissions or login again.');
      } else if (error.message.includes('500')) {
        console.error('Server error occurred while loading the PDF. Please try again later.');
      }
    }
  }

  @HostListener('document:keydown', ['$event'])
  onGlobalKeydown(event: KeyboardEvent): void {
    const key = (event.key || '').toLowerCase();
    const isCmdOrCtrl = event.ctrlKey || event.metaKey;
    if (isCmdOrCtrl && (key === 'p' || key === 's')) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  private resetToFirstPage(): void {
    if (this.pdfViewer) {
      try {
        this.pdfViewer.page = 1;
        console.log('Reset PDF to page 1');
      } catch (error) {
        console.warn('Could not reset PDF page:', error);
      }
    }
  }

  closeDialog(): void {
    if (this.pdfViewer) {
      try {
        this.pdfViewer.ngOnDestroy();
      } catch (error) {
        console.warn('Error during PDF viewer cleanup on close:', error);
      }
    }
    
    this.visible = false;
    this.dialogRef.close();
  }
}